import React, { useEffect, useState } from 'react';
import { Modal, Input, Form, Tooltip, Typography, message } from 'antd';
import { PencilSquareIcon, TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import * as yup from 'yup';
import { useFormik } from 'formik';
import InputMask from "react-input-mask";
import {
  ActionButton,
  DataTable,
  PageCard,
  PageShell,
  PageToolbar,
  RowIconButton,
  StatusBadge,
} from "../../components/ui";

const createValidationSchema = (t) => yup.object({
  name: yup.string().required(t('doctors.nameRequired')),
  login: yup.string().required(t('doctors.loginRequired')),
  password: yup.string().required(t('doctors.passwordRequired')),
  confirmPassword: yup.string()
      .oneOf([yup.ref('password'), null], t('doctors.passwordMismatch'))
      .required(t('doctors.confirmPasswordRequired')),
  phone: yup.string().required(t('doctors.phoneRequired')),
});

const editValidationSchema = (t) => yup.object({
  name: yup.string().required(t('doctors.nameRequired')),
  phone: yup.string().required(t('doctors.phoneRequired')),
});

export default function Doctors() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      login: "",
      password: "",
      confirmPassword: "",
      roles: [],
      phone: ""
    },
    validationSchema: editUser ? editValidationSchema(t) : createValidationSchema(t),
    onSubmit: (values) => {
      if (editUser) {
        updateUser(editUser.id, values);
      } else {
        createUser(values);
      }
    },
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/admin/users");
      const doctorUsers = response.data.data.filter(user => user.roles.includes('doctor'));
      setUsers(doctorUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData) => {
    setLoading(true);
    try {
      // Remove non-numeric characters from the phone number
      const formattedPhone = userData.phone.replace(/\D/g, ''); // Removes non-numeric characters
      const newUser = {
        ...userData,
        phone: formattedPhone,  // Set the formatted phone number
        roles: 'doctor'  // Set the role to doctor explicitly
      };
      const response = await axiosInstance.post('/admin/users', newUser);
      message.success(t('doctors.created'));
      setUsers([...users, response.data.data]);
      closeModal();
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response && error.response.data && error.response.data.message) {
        formik.setFieldError('phone', t('doctors.phoneTaken'));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updatedUserData) => {
    setLoading(true);
    try {
      // Remove non-numeric characters from the phone number
      const formattedPhone = updatedUserData.phone.replace(/\D/g, ''); // Removes non-numeric characters

      const fieldsToUpdate = {
        ...updatedUserData,
        phone: formattedPhone,  // Set the formatted phone number
        roles: 'doctor'  // Always set the role to doctor
      };

      const response = await axiosInstance.put(`/admin/users/${userId}`, fieldsToUpdate);
      message.success(t('doctors.updated'));
      setUsers(users.map(user => (user.id === userId ? response.data.data : user)));
      closeModal();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      message.success(t('doctors.deleted'));
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setEditUser(null);
    formik.resetForm();
  };

  const openModal = () => {
    setIsModalVisible(true);
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    formik.setValues({
      name: user.name,
      login: user.login,
      password: "",
      confirmPassword: "",
      roles: user.roles.map(role => ({ label: role, value: role })),
      phone: user.phone
    });
    setIsModalVisible(true);
  };

  const renderRoles = (roles) => (
      <div className="flex flex-wrap items-center gap-1">
        {roles.map((role, index) => (
            <StatusBadge key={index} tone="brand">{role}</StatusBadge>
        ))}
      </div>
  );

  const columns = [
    {
      title: t('doctors.fio'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
          <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>
      ),
    },
    {
      title: t('doctors.login'),
      dataIndex: 'login',
      key: 'login',
      render: (login) => <span className="text-sm text-slate-700">{login}</span>,
    },
    {
      title: t('doctors.colPhone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 160,
      render: (phone) => (
          <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{phone}</span>
      ),
    },
    {
      title: t('doctors.colRole'),
      dataIndex: 'roles',
      key: 'roles',
      width: 160,
      render: renderRoles,
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (_, user) => (
          <div className="flex items-center gap-0.5">
            <Tooltip title={t('doctors.edit')}>
              <RowIconButton onClick={() => openEditDialog(user)}>
                <PencilSquareIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
            <Tooltip title={t('doctors.delete')}>
              <RowIconButton onClick={() => deleteUser(user.id)}>
                <TrashIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
          </div>
      ),
    },
  ];

  const renderMobileCard = (user) => (
      <div>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#1D2C7B] truncate">{user.name}</span>
            <span className="block text-xs text-slate-500 mt-0.5">{user.login}</span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <RowIconButton onClick={() => openEditDialog(user)} aria-label={t('doctors.edit')}>
              <PencilSquareIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
            <RowIconButton onClick={() => deleteUser(user.id)} aria-label={t('doctors.delete')}>
              <TrashIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
          </div>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {renderRoles(user.roles)}
          <a href={`tel:+${user.phone}`} className="text-xs text-[#1D2C7B] tabular-nums">
            {user.phone}
          </a>
        </div>
      </div>
  );

  return (
      <PageShell>
        <PageCard>
          <PageToolbar
              title={t('doctors.title')}
              count={users.length}
              actions={
                <ActionButton icon={<UserPlusIcon className="w-4 h-4" />} onClick={openModal}>
                  {t('doctors.add')}
                </ActionButton>
              }
          />

          <DataTable
              columns={columns}
              dataSource={users}
              loading={loading}
              mobileCard={renderMobileCard}
              scrollX={760}
              pagination={{
                showSizeChanger: false,
                position: ['bottomCenter'],
                className: 'px-4 pb-3',
              }}
          />
        </PageCard>

        <Modal
            centered
            width="100%"
            style={{ maxWidth: 520 }}
            title={editUser ? t('doctors.edit') : t('doctors.create')}
            visible={isModalVisible}
            onCancel={closeModal}
            onOk={formik.handleSubmit}
            confirmLoading={loading}
            okText={t('common.save')}
            cancelText={t('common.cancel')}
        >
          <Form layout="vertical">
            <Form.Item label={t('doctors.fio')} validateStatus={formik.touched.name && formik.errors.name ? 'error' : ''}>
              <Input
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
              />
              {formik.touched.name && formik.errors.name && <Typography.Text type="danger">{formik.errors.name}</Typography.Text>}
            </Form.Item>
            <Form.Item label={t('doctors.login')} validateStatus={formik.touched.login && formik.errors.login ? 'error' : ''}>
              <Input
                  name="login"
                  value={formik.values.login}
                  onChange={formik.handleChange}
              />
              {formik.touched.login && formik.errors.login && <Typography.Text type="danger">{formik.errors.login}</Typography.Text>}
            </Form.Item>
            {!editUser && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <Form.Item label={t('doctors.password')} validateStatus={formik.touched.password && formik.errors.password ? 'error' : ''}>
                    <Input.Password
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.password && formik.errors.password && <Typography.Text type="danger">{formik.errors.password}</Typography.Text>}
                  </Form.Item>
                  <Form.Item label={t('doctors.passwordRepeat')} validateStatus={formik.touched.confirmPassword && formik.errors.confirmPassword ? 'error' : ''}>
                    <Input.Password
                        name="confirmPassword"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && <Typography.Text type="danger">{formik.errors.confirmPassword}</Typography.Text>}
                  </Form.Item>
                </div>
            )}
            <Form.Item label={t('doctors.phone')} validateStatus={formik.touched.phone && formik.errors.phone ? 'error' : ''}>
              <InputMask
                  mask="+\9\9\8 (99) 999-99-99"
                  maskChar="_"
                  className="h-10"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  name="phone"
              >
                {(inputProps) => (
                    <Input
                        {...inputProps}
                        prefix={<img src="/icons8-узбекистан-48.png" alt="" className="w-6 h-6" />}
                        placeholder="+998 (__) ___-__-__"
                    />
                )}
              </InputMask>
              {formik.touched.phone && formik.errors.phone && (
                  <Typography.Text type="danger">{formik.errors.phone}</Typography.Text>
              )}
            </Form.Item>

          </Form>
        </Modal>
      </PageShell>
  );
}
